import { type FC, useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useProductSyncRuns } from '../../hooks/useSyncRuns';
import {
    ErrorBanner,
    Notable,
    NoticeBanner,
    Panel,
    RunButton,
    StatusBadge,
    Table,
    TriggerBadge,
} from './syncUi';
import { formatDateTime, formatDuration, formatNumber, formatRelative } from './syncFormat';

/**
 * Product sync: Arch's catalogue, swept once a day.
 *
 * A different shape to the stock sync — global rather than per store, paged, and
 * with a reconcile phase that checks whether products missing from the feed have
 * genuinely gone. Shown as its own page rather than shoehorned into a shared table.
 */
const ProductSyncPage: FC = () => {
    const { runs, loading, error, notice, triggering, fetchRuns, run, dismissNotice } = useProductSyncRuns();

    useEffect(() => {
        void fetchRuns();
    }, [fetchRuns]);

    const latest = runs[0];
    // Only a finished run has a count, so a run in progress shows the last one we know.
    const lastLive = runs.find((r) => r.live_products !== null)?.live_products ?? null;
    // One sweep at a time — a second would be told it is busy anyway.
    const anyRunning = runs.some((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING');

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-semibold text-stone-900">Product Sync</h2>
                    <p className="mt-1 text-sm text-stone-500">
                        The product catalogue pulled from Arch, swept daily in the early hours, then
                        reconciled to catch products that have been withdrawn.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => void fetchRuns()}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    {/* Neither is disabled while a run is live. The functions answer
                        `busy` themselves, and reconcile answers `no_run` when there is
                        nothing to work on — both come back as a notice. Guessing here
                        would only stop someone resuming a run that has stalled. */}
                    <RunButton
                        onClick={() => void run('reconcile')}
                        busy={triggering === 'reconcile'}
                        disabled={triggering !== null}
                        label="Reconcile"
                        busyLabel="Reconciling…"
                        title="Check products missing from the feed before taking any off sale"
                    />
                    <RunButton
                        onClick={() => void run('sync')}
                        busy={triggering === 'sync'}
                        disabled={triggering !== null}
                        label={anyRunning ? 'Resume sweep' : 'Sync now'}
                        busyLabel="Syncing…"
                    />
                </div>
            </div>

            {error && <ErrorBanner message={error} />}
            {notice && <NoticeBanner message={notice} onDismiss={dismissNotice} />}

            {latest && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                        { label: 'Last run', value: formatRelative(latest.created_at) },
                        { label: 'Status', value: latest.status },
                        { label: 'Live for sale', value: formatNumber(lastLive) },
                        { label: 'Products written', value: formatNumber(latest.upserted) },
                        { label: 'Unchanged', value: formatNumber(latest.unchanged) },
                    ].map((stat) => (
                        <div key={stat.label} className="rounded-2xl border border-stone-200 bg-white shadow-sm px-5 py-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{stat.label}</p>
                            <p className="mt-1 text-lg font-semibold text-stone-900">{stat.value}</p>
                        </div>
                    ))}
                </div>
            )}

            <Panel
                title="Recent runs"
                description="Newest first. Written is everything Arch sent, sellable or not. Live is what was left on sale when the run finished. Unchanged counts products the sweep saw but did not need to write."
            >
                <Table
                    headers={['Started', 'Kind', 'Phase', 'Trigger', 'Status', 'Pages', 'Fetched', 'Written', 'Live', 'Unchanged', 'Rejected', 'Took', 'Error']}
                >
                    {loading && runs.length === 0 ? (
                        <tr><td colSpan={13} className="py-10 px-6 text-stone-400">Loading…</td></tr>
                    ) : runs.length === 0 ? (
                        <tr>
                            <td colSpan={13} className="py-10 px-6 text-stone-400">
                                No runs yet. The sweep runs daily in the early hours.
                            </td>
                        </tr>
                    ) : (
                        runs.map((run) => (
                            <tr key={run.id} className="hover:bg-stone-50/50 transition-colors">
                                <td className="py-4 px-6 text-stone-600 whitespace-nowrap">
                                    {formatDateTime(run.started_at ?? run.created_at)}
                                </td>
                                <td className="py-4 px-6 text-stone-600">{run.kind}</td>
                                <td className="py-4 px-6 text-stone-500">{run.phase ?? '—'}</td>
                                <td className="py-4 px-6"><TriggerBadge trigger={run.trigger_type} /></td>
                                <td className="py-4 px-6"><StatusBadge status={run.status} /></td>
                                <td className="py-4 px-6 text-stone-600">{formatNumber(run.pages_done)}</td>
                                <td className="py-4 px-6 text-stone-600">{formatNumber(run.fetched)}</td>
                                <td className="py-4 px-6 text-stone-900 font-medium">{formatNumber(run.upserted)}</td>
                                <td className="py-4 px-6 text-stone-900 font-medium">{formatNumber(run.live_products)}</td>
                                <td className="py-4 px-6 text-stone-500">{formatNumber(run.unchanged)}</td>
                                <td className="py-4 px-6"><Notable value={run.rejected} /></td>
                                <td className="py-4 px-6 text-stone-500 whitespace-nowrap">{formatDuration(run.duration_ms)}</td>
                                <td className="py-4 px-6 text-rose-700 max-w-xs truncate" title={run.last_error ?? ''}>
                                    {run.last_error ?? ''}
                                </td>
                            </tr>
                        ))
                    )}
                </Table>
            </Panel>

            <Panel
                title="Reconciliation"
                description="Products missing from the feed are looked up individually before anything is taken off sale."
            >
                <Table headers={['Started', 'Status', 'Checked', 'Taken off sale', 'Not found at Arch', 'Lookup failed']}>
                    {runs.filter((r) => r.checked > 0 || r.deactivated > 0 || r.lookup_failed > 0).length === 0 ? (
                        <tr>
                            <td colSpan={6} className="py-10 px-6 text-stone-400">
                                No reconciliation activity yet.
                            </td>
                        </tr>
                    ) : (
                        runs
                            .filter((r) => r.checked > 0 || r.deactivated > 0 || r.lookup_failed > 0)
                            .map((run) => (
                                <tr key={run.id} className="hover:bg-stone-50/50 transition-colors">
                                    <td className="py-4 px-6 text-stone-600 whitespace-nowrap">
                                        {formatDateTime(run.started_at ?? run.created_at)}
                                    </td>
                                    <td className="py-4 px-6"><StatusBadge status={run.status} /></td>
                                    <td className="py-4 px-6 text-stone-600">{formatNumber(run.checked)}</td>
                                    <td className="py-4 px-6"><Notable value={run.deactivated} /></td>
                                    <td className="py-4 px-6 text-stone-600">{formatNumber(run.not_found)}</td>
                                    {/* A lookup that never got an answer is not evidence a product is gone. */}
                                    <td className="py-4 px-6"><Notable value={run.lookup_failed} /></td>
                                </tr>
                            ))
                    )}
                </Table>
            </Panel>
        </div>
    );
};

export default ProductSyncPage;
